import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import ErrorBoundary from "@/app/shared/ErrorBoundary";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import SelectableCard from "@/design/components/SelectableCard";
import FormGroup from "@/design/forms/FormGroup";
import InputCopy from "@/design/forms/InputCopy";
import InputNumeric from "@/design/forms/InputNumeric";
import InputSelect from "@/design/forms/InputSelect";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import { number, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { formatToMac, toggleLoadingState } from "@/utils/utility";
import Valkyrie, {
  viArrowLeft,
  viCircleInfo,
  viFloppyDisk,
  viPeople,
  viPerson,
  viPhoneOffice,
  viVolume3,
} from "@sippy-platform/valkyrie";

import {
  Alert,
  Button,
  CardHeader,
  Container,
  FormHelperText,
  Grid,
  IconButton,
  Slider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import DeviceCard from "./DeviceCard";
import Handsets from "./handsets/Handsets";
import provisioningReducer from "./provisioningReducer";
import UserSettings from "./UserSettings";

function Provisioning() {
  const { mac, pbxId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const pbx = useSelector((state) => state.domain.pbx);
  const userStore = useSelector((state) => state.domain.users);

  const [specs, setSpecs] = useState({});
  const [user, setUser] = useState({});
  const [tab, setTab] = useState(0);
  const [vlan, setVlan] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [provisioningClient, setProvisioningClient] = useState("Yealink");
  const [previousMac, setPreviousMac] = useState(mac);

  const handleTab = (event, newTab) => setTab(newTab);

  // We do this to always have the flex bot when necessary
  const provisioningStore = useSelector((state) => state.domain.provisionings);

  // Get general data
  useEffect(() => {
    Promise.resolve(Api.getPbx());

    if (mac !== "new") {
      Promise.all([Api.getProvisioningUsers(), Api.getUsers(), Api.getLineKeyTemplates()]).then((response) => {
        setAvailableUsers(response[0].data.sippeers);
      });
    }
  }, [mac]);

  const {
    value: provisioning,
    dispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: {},
    reducer: provisioningReducer,
    validator: () => ({
      vlanWanId: number()
        .required("VLAN WAN ID must be between 1 and 4094, or 0 to be disabled.", { condition: vlan })
        .min(0, "VLAN WAN ID must be between 1 and 4094, or 0 to be disabled.", { condition: vlan })
        .max(4094, "VLAN WAN ID must be between 1 and 4094, or 0 to be disabled.", { condition: vlan }),
      vlanWanPriority: number()
        .required("VLAN WAN Priority must be between 0 and 7.", {
          condition: vlan,
        })
        .min(0, "VLAN WAN Priority must be between 0 and 7.", {
          condition: vlan,
        })
        .max(7, "VLAN WAN Priority must be between 0 and 7.", {
          condition: vlan,
        }),
      vlanPcId: number()
        .required("VLAN PC ID must be between 1 and 4094, or 0 to be disabled.", { condition: vlan })
        .min(0, "VLAN PC ID must be between 1 and 4094, or 0 to be disabled.", {
          condition: vlan,
        })
        .max(4094, "VLAN PC ID must be between 1 and 4094, or 0 to be disabled.", { condition: vlan }),
      vlanPcPriority: number()
        .required("VLAN PC Priority must be between 0 and 7.", {
          condition: vlan,
        })
        .min(0, "VLAN PC Priority must be between 0 and 7.", {
          condition: vlan,
        })
        .max(7, "VLAN PC Priority must be between 0 and 7.", {
          condition: vlan,
        }),
      firmwareUpdateUrl: string().pattern(
        "http[s]?:\\/\\/(www\\.)?(.*)?\\/?(.)*",
        "The firmware update URL isn't valid.",
      ),
    }),
    onSubmit: () => Api.putProvisioning(provisioning),
    onSuccess: (response) => {
      if (!provisioning.id) {
        dispatch({ type: "flexBot", value: response.data.flexBot });
      }

      Promise.all([Api.getProvisionings(), Api.getProvisioningUsers()]).then((response) => {
        setAvailableUsers(response[1].data.sippeers);
      });
    },
  });

  // VLAN settings
  const [vlanSettings, setVlanSettings] = useState({});
  const formHandlerVlan = (property, value) => {
    setVlanSettings((prev) => ({
      ...prev,
      [property]: value == null || value === "" ? null : Number(value),
    }));
  };

  const saveVlanSettings = () => {
    dispatch({ type: "vlanConfig", value: vlanSettings });
    setVlanSettings({});
    setSaveState(SaveState.UNSAVED);
  };

  // Firmware settings
  const [firmwareSettings, setFirmwareSettings] = useState({});
  const formHandlerFirmware = (property, value) => {
    setFirmwareSettings((prev) => ({ ...prev, [property]: value }));
  };

  const saveFirmwareSettings = () => {
    dispatch({ type: "firmwareConfig", value: firmwareSettings });
    setFirmwareSettings({});
    setSaveState(SaveState.UNSAVED);
  };

  if (mac !== previousMac) {
    setPreviousMac(mac);
    setTab(0);
    setVlan(false);
    setVlanSettings({});
    setFirmwareSettings({});
  }

  useEffect(() => {
    if (mac === "new") {
      Api.cancelActiveGetRequests();
      return;
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getProvisioning({ mac }))
      .then((response) => {
        dispatch({ type: "set", value: response.data.phone });
        setVlan(Boolean(response.data.phone.vlanWanId || response.data.phone.vlanPcId));

        if (response.data?.phone.sippeer || response.data?.phone.flexUser) {
          Promise.resolve(Api.getUser(response.data?.phone.sippeer || response.data?.phone.flexUser)).then(
            (response) => {
              setUser(response.data.sippeer);
            },
          );
        } else {
          setUser({});
        }

        Promise.resolve(Api.getDevice({ type: response.data?.phone.type }))
          .then((_response) => setSpecs(_response.data?.device))
          .catch(() => setSpecs(null));
      })
      .finally(() => toggleLoadingState(false));
  }, [dispatch, mac]);

  // Device data
  const dataDevice = useMemo(
    () => provisioningStore.list.find((device) => device.mac === provisioning.mac),
    [provisioning.mac, provisioningStore.list],
  );

  // Handle changes
  const formHandler = (property, value) => {
    switch (property) {
      case "sippeer": {
        const user = userStore.list.find((_user) => value == _user.id);

        dispatch({ type: property, user });
        setUser(user ?? {});
        break;
      }
      case "vlan": {
        setVlan((prevVlan) => !prevVlan);
        setVlanSettings({
          vlanWanId: 0,
          vlanWanPriority: 0,
          vlanPcId: 0,
          vlanPcPriority: 0,
        });
        break;
      }
      default:
        dispatch({ type: property, value });
    }

    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getProvisionings()).then(() => navigate(`/pbx/${pbxId}/provisioning`));

    Promise.resolve(Api.getProvisioningUsers()).then((response) => {
      setAvailableUsers(response.data.sippeers);
    });
  }

  // Microphone gain
  const microphoneGainScale = useMemo(() => {
    switch (specs?.brand) {
      case "Fanvil":
        return {
          min: { value: 1, label: "1" },
          default: { value: 5, label: "5 (default)" },
          max: { value: 9, label: "9" },
        };
      case "Yealink":
      default:
        return {
          min: { value: -50, label: "-50%" },
          default: { value: 0, label: "0% (default)" },
          max: { value: 50, label: "50%" },
        };
    }
  }, [specs?.brand]);

  return mac !== "new" ? (
    <Container>
      <title>{`Provisioning \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          tab={tab}
          changeTab={handleTab}
          tabs={[
            { label: "Device", icon: viPhoneOffice, value: 0, id: "device" },
            {
              label: "Meta",
              icon: viCircleInfo,
              value: 1,
              id: "meta",
              disabled: !specs,
            },
            {
              label: "User",
              icon: viPerson,
              value: 2,
              id: "user",
              disabled: !specs,
            },
          ].filter((_t) => !(_t.label === "User" && !user.id))}
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} />
                <Avatar text={formatToMac(dataDevice?.mac)} icon={viPhoneOffice} />
              </IconButton>
            ) : (
              <Avatar text={formatToMac(dataDevice?.mac)} icon={viPhoneOffice} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                key={provisioning?.mac || "nk"}
                isActionable={!!provisioning}
                onDelete={() => Api.deleteProvisioning(provisioning)}
                name={provisioning?.mac && formatToMac(provisioning?.mac)}
                deleteInfo="Are you sure you want to delete this device? This can't be undone."
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={
            dataDevice
              ? dataDevice.sippeer !== null
                ? `${dataDevice.sippeer?.exten} \u00b7 ${dataDevice.sippeer?.name}`
                : dataDevice.flexDesk === 1
                  ? `${dataDevice.flexBot ? dataDevice.flexBot.name : "Getting ID..."} \u00b7 ${dataDevice.flexUser !== null ? dataDevice.flexUser.name : "No user"}`
                  : formatToMac(dataDevice.mac)
              : "Loading..."
          }
          subheader={dataDevice ? `${dataDevice.phone} ${dataDevice.type}` : undefined}
        />
        {!specs ? (
          <DeviceCard provisioning={provisioning} specs={specs} />
        ) : (
          <>
            {tab === 0 && (
              <>
                <DeviceCard provisioning={provisioning} specs={specs} />
                <FormGroup title="Device mode">
                  <Grid size={{ xs: 12, sm: 6, xl: 4 }}>
                    <SelectableCard
                      selected={!provisioning.flexDesk}
                      onClick={() => formHandler("flexDesk", 0)}
                      icon={viPerson}
                      title={specs.accounts === 1 ? "Default user" : "Handset 1"}
                      description={
                        specs.accounts === 1
                          ? "Add a single user to this device."
                          : "Add the primary user for this handset."
                      }
                    >
                      <InputSelect
                        id="sippeer"
                        label="Default user"
                        onChange={formHandler}
                        value={provisioning.sippeer ? provisioning.sippeer?.id : 0}
                        selectValue="id"
                        selects={provisioning.sippeer ? [provisioning.sippeer, ...availableUsers] : availableUsers}
                        getOptionLabel={(option) => `${option.exten} \u00b7 ${option.name}`}
                        hasEmpty={0}
                        disabled={!!provisioning.flexDesk}
                        sx={{ mt: 2 }}
                      />
                    </SelectableCard>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, xl: 4 }}>
                    <SelectableCard
                      selected={!!provisioning.flexDesk}
                      onClick={() => formHandler("flexDesk", 1)}
                      icon={viPeople}
                      title="Flex desking"
                      description={
                        specs.flexDesk === "yes"
                          ? "Allow Flex desk-enabled users to login on this device."
                          : "Flex desking is not supported on this device."
                      }
                      disabled={specs.flexDesk === "no"}
                    />
                  </Grid>
                </FormGroup>
                <FormGroup title="Language">
                  <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                    <InputSelect
                      id="language"
                      label="Default language"
                      onChange={formHandler}
                      value={provisioning.language}
                      selectValue="value"
                      selects={[
                        { value: "Dutch" },
                        { value: "English" },
                        { value: "French" },
                        { value: "German" },
                        { value: "Italian" },
                        { value: "Polish" },
                        { value: "Portuguese" },
                        { value: "Russian" },
                        { value: "Spanish" },
                        { value: "Turkish" },
                      ]}
                      getOptionLabel={(option) => option.value}
                      error={errors}
                      helperText="The language the device uses when no account is available."
                    />
                  </Grid>
                </FormGroup>
                {specs.accounts > 1 && (
                  <ErrorBoundary>
                    <FormGroup title="Handsets" id="handsetsCard">
                      <Handsets provisioning={provisioning} specs={specs} provisioningDispatch={dispatch} />
                    </FormGroup>
                  </ErrorBoundary>
                )}
                {!!specs && (
                  <>
                    {specs.handsetSendVolume === "yes" && (
                      <FormGroup title="Microphone gain">
                        <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                          <Grid
                            container
                            spacing={2}
                            sx={{
                              alignItems: "flex-start",
                            }}
                          >
                            <Grid>
                              <Valkyrie icon={viVolume3} />
                            </Grid>
                            <Grid size="grow">
                              <Slider
                                value={provisioning.handsetSendVolume || microphoneGainScale.default.value}
                                onChange={(e, value) => formHandler("handsetSendVolume", value)}
                                valueLabelDisplay="auto"
                                aria-label="Micrphone volume gain"
                                min={microphoneGainScale.min.value}
                                max={microphoneGainScale.max.value}
                                marks={[microphoneGainScale.min, microphoneGainScale.default, microphoneGainScale.max]}
                                error={errors}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </FormGroup>
                    )}
                    <FormGroup title="Port">
                      <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                        <InputSelect
                          id="sipPort"
                          label="SIP port"
                          onChange={formHandler}
                          value={provisioning.sipPort}
                          selectValue="value"
                          selects={[{ value: "5060" }, { value: "5062" }]}
                          getOptionLabel={(option) => option.value}
                          error={errors}
                          helperText="The port on which your SIP traffic occurs, change this if you have issues with SIP ALG."
                        />
                      </Grid>
                    </FormGroup>
                    <FormGroup title="VLAN" collapsible={true}>
                      <Grid size={12}>
                        <Alert severity="warning">
                          VLAN settings won't save automatically to make sure you don't accidentally push an incorrect
                          configuration which may cause the device to not accept any further changes you make in these
                          settings.
                        </Alert>
                      </Grid>
                      <Grid size={12}>
                        <InputToggle
                          id="vlan"
                          onChange={formHandler}
                          states={[false, true]}
                          value={vlan}
                          primary="Use custom VLAN settings"
                          secondary="Changing this setting may result in this device being unable to make a connection to the internet and to take any further configuration changes."
                          error={errors}
                        />
                      </Grid>
                      {vlan && (
                        <>
                          <Grid size={12}>
                            <Typography variant="h6" sx={{ m: 0 }}>
                              WAN settings
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                            <InputNumeric
                              required
                              id="vlanWanId"
                              label="WAN VLAN"
                              onChange={formHandlerVlan}
                              value={vlanSettings.vlanWanId ?? provisioning.vlanWanId}
                              min={0}
                              max={4094}
                              helperText="A value between 1 and 4094, or 0 to disable."
                              error={errors}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                            <InputNumeric
                              required
                              id="vlanWanPriority"
                              label="WAN VLAN Priority"
                              onChange={formHandlerVlan}
                              value={vlanSettings.vlanWanPriority ?? provisioning.vlanWanPriority}
                              min={0}
                              max={7}
                              helperText="A value between 0 and 7."
                              error={errors}
                            />
                          </Grid>
                          <Grid size={12}>
                            <Typography variant="h6" sx={{ m: 0 }}>
                              PC settings
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                            <InputNumeric
                              required
                              id="vlanPcId"
                              label="PC VLAN"
                              onChange={formHandlerVlan}
                              value={vlanSettings.vlanPcId ?? provisioning.vlanPcId}
                              min={0}
                              max={4094}
                              helperText="A value between 1 and 4094, or 0 to disable."
                              error={errors}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                            <InputNumeric
                              required
                              id="vlanPcPriority"
                              label="PC VLAN Priority"
                              onChange={formHandlerVlan}
                              value={vlanSettings.vlanPcPriority ?? provisioning.vlanPcPriority}
                              min={0}
                              max={7}
                              helperText="A value between 0 and 7."
                              error={errors}
                            />
                          </Grid>
                        </>
                      )}
                      <Grid style={{ display: "flex", justifyContent: "end" }} size={12}>
                        <Button
                          variant="contained"
                          onClick={saveVlanSettings}
                          disabled={Object.keys(vlanSettings).length === 0}
                          startIcon={<Valkyrie icon={viFloppyDisk} />}
                        >
                          Save
                        </Button>
                      </Grid>
                    </FormGroup>
                    <FormGroup title="Firmware" collapsible={true}>
                      <Grid size={12}>
                        <Alert severity="warning">
                          Changing the firmware of your device is at your own risk. We advise against downgrading your
                          device's firmware. Some firmware versions may not work with Sippy.
                        </Alert>
                      </Grid>
                      <Grid size={12}>
                        <InputText
                          id="firmwareUpdateUrl"
                          label="Firmware update URL"
                          onChange={formHandlerFirmware}
                          value={firmwareSettings.firmwareUpdateUrl ?? provisioning.firmwareUpdateUrl}
                          helperText="The URL to a firmware update file to update the firmware of the device."
                          error={errors}
                        />
                      </Grid>
                      <Grid style={{ display: "flex", justifyContent: "end" }} size={12}>
                        <Button
                          variant="contained"
                          onClick={saveFirmwareSettings}
                          disabled={Object.keys(firmwareSettings).length === 0}
                          startIcon={<Valkyrie icon={viFloppyDisk} />}
                        >
                          Save
                        </Button>
                      </Grid>
                    </FormGroup>
                  </>
                )}
              </>
            )}
            {tab === 1 && (
              <FormGroup title="Meta">
                <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                  <InputText
                    id="serialNumber"
                    label="Serial number"
                    onChange={formHandler}
                    value={provisioning.serialNumber}
                    error={errors}
                    helperText="The serial number of this device."
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                  <InputText
                    id="description"
                    label="Description"
                    onChange={formHandler}
                    value={provisioning.description}
                    error={errors}
                    helperText="A short description for this device."
                  />
                </Grid>
              </FormGroup>
            )}
            {tab === 2 && <UserSettings user={user} specs={specs} />}
          </>
        )}
      </Stack>
    </Container>
  ) : (
    <Container>
      <title>{`Provisioning \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard>
          <CardHeader
            avatar={
              !matches ? (
                <IconButton
                  component={NavLink}
                  to={"/provisioning"}
                  avatar={<Avatar text="Manage devices" icon={viPhoneOffice} />}
                >
                  <Valkyrie icon={viArrowLeft} />
                </IconButton>
              ) : (
                <Avatar text="Manage devices" icon={viPhoneOffice} />
              )
            }
            title="Manage devices"
          />
        </HeaderCard>
        <FormGroup title="Add a device">
          <Grid size={12}>
            <Stack
              direction="row"
              sx={{
                alignItems: "stretch",
                gap: 1,
                height: 40,
              }}
            >
              <InputSelect
                id="client"
                label="Client"
                error={errors}
                onChange={(_, value) => setProvisioningClient(value)}
                value={provisioningClient}
                selects={pbx.provisioningUrls}
                selectValue="client"
                getOptionLabel={(option) => option.client}
                sx={{ width: 180 }}
              />
              <InputCopy
                id="provisioningUrl"
                label="Provisioning URL"
                value={pbx.provisioningUrls?.find((_urls) => _urls.client === provisioningClient)?.url}
              />
            </Stack>
            <FormHelperText>Paste this url in your device to start provisioning it.</FormHelperText>
          </Grid>
        </FormGroup>
      </Stack>
    </Container>
  );
}

export default Provisioning;
